import { useCallback, useEffect, useState } from 'react';
import { getConversation, getMessage } from '../../service/ChatService';
import { useChat } from './ChatProvider';
import ThreadPanel from './ui/ThreadPanel';
import { ChatBusinessProvider } from './ChatBusinessContext';

export default function InboxThreadDetail({
  item,
  onClose,
  onChanged,
  onDirty,
  onUnavailable,
  active,
  revision,
  onForward,
  renderMessageActions,
}) {
  const chat = useChat();
  const [data, setData] = useState(null),
    [error, setError] = useState(''),
    [retry, setRetry] = useState(0);
  const { refreshConversations } = chat;
  useEffect(() => {
    const abort = new AbortController();
    const timer = setTimeout(() => {
      void Promise.all([
        getMessage(item.id, abort.signal),
        getConversation(item.conversation_id, abort.signal),
      ])
        .then(([{ message }, conversation]) => {
          if (abort.signal.aborted) return;
          if (message.deleted_at) {
            onUnavailable();
            return;
          }
          const next = { root: message, conversation };
          setData((current) =>
            JSON.stringify(current) === JSON.stringify(next) ? current : next,
          );
          setError('');
        })
        .catch((cause) => {
          if (!abort.signal.aborted) {
            if ([401, 403, 404].includes(cause.status)) {
              onUnavailable();
              return;
            }
            setError(cause.message || 'Unable to open thread');
          }
        });
    }, 120);
    return () => {
      clearTimeout(timer);
      abort.abort();
    };
  }, [item.id, item.conversation_id, revision, retry, onUnavailable]);
  const changed = useCallback(async () => {
    await onChanged();
    await refreshConversations();
    setRetry((value) => value + 1);
  }, [onChanged, refreshConversations]);
  return (
    <>
      {error ? (
        <div className="inbox-error" role="alert">
          {error}
          <button type="button" onClick={() => setRetry((value) => value + 1)}>
            Retry
          </button>
        </div>
      ) : null}
      {data ? (
        <ChatBusinessProvider
          currentUser={chat.currentUser}
          conversationId={item.conversation_id}
          rootIds={[item.id]}
        >
          <ThreadPanel
            active={active}
            root={data.root}
            conversation={data.conversation}
            currentUser={chat.currentUser}
            invalidation={chat.invalidations[item.conversation_id] || 0}
            messageMutationEvents={
              chat.messageMutationEvents?.[item.conversation_id] || {}
            }
            onClose={onClose}
            onRootChanged={changed}
            onForbidden={changed}
            cacheScope={chat.cacheScope}
            onForward={onForward}
            renderMessageActions={renderMessageActions}
            onDirty={onDirty}
          />
        </ChatBusinessProvider>
      ) : !error ? (
        <p className="inbox-empty" role="status">
          Loading thread…
        </p>
      ) : null}
    </>
  );
}
